import { Api, unitField } from "@vzbl/integrations";
import pluralizer from "pluralize";
import { dateToString, formatCalendarValue, isDateParts } from "./date";
import { valueToNumber } from "pages/[orgId]/explore/lib/common";
import { TimeUnit } from "pages/[orgId]/explore/lib/FormattingControls";
import { ValueFormatter, ValueType } from "./formatters";
import { Field } from "src/search/types";

const CACHE: Record<string, string> = {};

export function attributeToTitle(attribute = ""): string {
  // just making sure...
  if (typeof attribute !== "string") {
    return attributeToTitle(JSON.stringify(attribute));
  }

  if (CACHE[attribute]) return CACHE[attribute];

  let result = measureToTitle(attribute);
  const parts = result.split(/[._\-]/);
  if (parts.length > 1)
    return parts.map((part) => attributeToTitle(part)).join(" ");

  result = "";
  let idx = 0;
  let inUppers = true;
  for (idx = 0; idx < attribute.length; idx += 1) {
    const char = attribute.charAt(idx);
    if (idx === 0) result += char.toUpperCase();
    else {
      if (/[A-Z]/.test(char)) {
        result += inUppers ? char : ` ${char}`;
        inUppers = true;
      } else {
        result += char;
        inUppers = false;
      }
    }
  }
  result = result.replace(/ Of /g, " of ").replace(/(\d+)/g, " $1");
  return (CACHE[attribute] = result);
}

/**
 * Helper function to extract clean field name from a possibly complex field path
 */
export function extractCleanFieldName(field: string): string {
  const fieldLower = field.toLowerCase();
  // Extract actual field name from function wrapper like avg(Issue.vzblCycleTime)
  const actualField = fieldLower.match(/[A-Za-z]+\((.*?)\)/)?.[1] || fieldLower;
  // Remove any table prefixes (e.g. Issue.vzblCycleTime -> vzblCycleTime)
  return actualField.split(".").pop() || actualField;
}

export interface FieldParts {
  function?: string;
  target?: string;
  attribute: string;
}

// count(Event.uuid)
// -> Event Uuid Count
function measureToTitle(text: string) {
  const [_, measure, by] = text.match(/(.*)\((.*)\)/) || [];
  if (measure && by) return attributeToTitle(`${measure}.${by}`);
  return text;
}

export function fieldToTitle(field: {
  attribute: string;
  target: string;
  canonical?: boolean;
}): string {
  if (field.canonical) {
    return attributeToTitle(field.target);
  }
  if (field.attribute == unitField) {
    return attributeToTitle(field.target);
  }
  return `${attributeToTitle(field.target)} ${attributeToTitle(
    field.attribute
  )}`;
}

export function pluralize(text: string, count?: number): string {
  return pluralizer(text, count);
}

export function createId(): string {
  const id = Number(Math.round(Math.random() * 1000) + "" + Date.now())
    .toString(16)
    .replace(/^\d+/, "");
  return id === "" ? createId() : id;
}

export function formatColumnValue(
  value: any,
  colName: string,
  columnInfo: Api.Schema.ColumnInfo
): string {
  const col = columnInfo.find((c) => c.key == colName) || {
    type: "string",
    unit: undefined,
  };

  return ValueFormatter.format(Number(value), {
    field: colName,
    type: col.type as ValueType,
    unit: col.unit,
  });
}

export function unitTypeForCol(
  colName: string,
  columnInfo: Api.Schema.ColumnInfo
): { type: string; unit?: string; tuple: boolean } {
  const { type, unit, diffMethod } = columnInfo.find(
    (c) => c.key == colName
  ) || {
    type: "string",
    unit: undefined,
    tuple: false,
  };
  return { type, unit, tuple: diffMethod === "tuple" };
}

export function formatValue(
  value: any,
  type: "yearweek" | "yearmonthday" | "number" | string | undefined,
  unit: string | undefined,
  precision?: number | TimeUnit
): string {
  if (value == "__VZBL::null__") return type === "number" ? "0" : "n/a";
  if (
    unit &&
    ["yearweek", "yearmonthday", "yearmonth", "yearquarter"].includes(unit)
  ) {
    return formatCalendarValue(value, unit);
  }
  if (type == "number") {
    const asNum = Number(valueToNumber(value));
    const num = Number(
      typeof precision === "number" ? asNum.toPrecision(precision) : asNum
    );
    return ValueFormatter.formatWithUnit(
      num,
      unit,
      typeof precision === "string" ? precision : undefined
    );
  }
  if (type == "interval") {
    const [a, b] = `${value}`.split(",").slice(-2);
    const lower =
      a === "-Infinity" ? `<` : ValueFormatter.formatWithUnit(Number(a), unit);
    const upper =
      b === "Infinity" ? `<` : ValueFormatter.formatWithUnit(Number(b), unit);
    const hyphen = [a, b].some((v) => v?.includes("Infinity")) ? "" : "-";
    return ` ${lower} ${hyphen} ${upper}`;
  }
  if (isDateParts(value)) return dateToString(value) || "";
  return value;
}

type unitQuantity = { qty: number; label: string; selectable?: boolean };

const timeQuantities: Record<string, unitQuantity> = {
  millisecond: { qty: 1, label: "ms" },
  second: { qty: 1000, label: "s" },
  minute: { qty: 60_000, label: "min", selectable: true },
  hour: { qty: 3_600_000, label: "hr", selectable: true },
  day: { qty: 3_600_000 * 24, label: "day", selectable: true },
  week: { qty: 3_600_000 * 24 * 7, label: "wk", selectable: true },
};

const byteQuantities: Record<string, unitQuantity> = {
  kilobyte: { qty: 1024, label: "KB", selectable: true },
  megabyte: { qty: 1024 * 1024, label: "MB", selectable: true },
  gigabyte: { qty: 1024 * 1024 * 1024, label: "GB", selectable: true },
};

export const relativeUnitsFor = (unit: string) => {
  const realm = [timeQuantities, byteQuantities].find((r) => r[unit]);
  if (realm == null) {
    return null;
  }
  const qty = realm[unit].qty;
  const options = Object.values(realm).filter((v) => v.selectable);
  return { qty, options };
};

export function titleCase(text: string) {
  return text
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const upper = word.at(0)?.toUpperCase();
      const lower = word.slice(1);
      return `${upper}${lower}`;
    })
    .join(" ");
}

export function makeTitleTerser(
  selecting: string[] = []
): (title: string | null | undefined) => string {
  if (selecting.length === 0) return makeTitleTerser(["Event"]);

  const droppablePrefixes = selecting.reduce((a, c) => {
    const titled = attributeToTitle(c);
    // put the longer one first..
    const more = [`${titled} vzbl`, `${c} vzbl`, `${titled}`, `${c}`].map(
      (str) => `${str} `.toLowerCase()
    );
    return [...a, ...more]; // put the longer one first..
  }, [] as string[]);

  return (title: string | null | undefined) => {
    if (!title) return "";
    if (typeof title !== "string") {
      return String(title);
    }
    const lowerTitle = title.toLowerCase();
    const prefix = droppablePrefixes.find((candidate) =>
      lowerTitle.startsWith(candidate)
    );
    return titleCase(prefix ? title.slice(prefix.length) : title);
  };
}

// if it looks like an '@person' ask tag, extract the name.
// expected format is @[name email](identifier)
export function askAtPersonToName(searchTerm: string): string {
  if (!searchTerm.startsWith("@")) {
    return searchTerm;
  }
  const inBrackets = searchTerm.match(/@\[(.*?)]\((.*?)\)/);
  if (!inBrackets) {
    return searchTerm;
  }
  return inBrackets[1].split(" ").at(0)!;
}

export function askEntityExtraction(searchTerm: string) {
  const POUND_REGEX = /#{{(.*?)}}/;
  const match = searchTerm.match(POUND_REGEX);
  if (match !== undefined && match !== null) {
    const entity = match.at(1);
    const term =
      searchTerm.slice(0, match.index) +
      searchTerm.slice((match.index || 0) + match.at(0)!.length);
    return {
      entity,
      searchTerm: term.trim(),
    };
  }
  return {
    searchTerm,
  };
}

export function extractField(field: string): {
  target: string;
  attribute: string;
} {
  const [target = "", attribute = ""] = field?.split(".");
  return {
    target,
    attribute,
  };
}

/**
 * Formats an aggregation function name into a user-friendly label
 */
export function formatAggregationFunction(func: string): string {
  switch (func.toLowerCase()) {
    case "count": {
      return "# of";
    }
    case "avg": {
      return "Average";
    }
    case "sum": {
      return "Total";
    }
    case "min": {
      return "Min";
    }
    case "max": {
      return "Max";
    }
    default: {
      return attributeToTitle(func);
    }
  }
}

/**
 * Formats a field with an aggregation function into a user-friendly label
 * e.g., "count(Activity.Actor)" -> "# of Actors", "count(Issue.uuid)" -> "# of Issues"
 */
export function formatAggregationFieldLabel(field: string): string {
  const functionMatch = field.match(/([A-Za-z]+)\((.*?)\)/);
  if (functionMatch) {
    const [_, func, fieldPath] = functionMatch;
    const cleanField = extractCleanFieldName(fieldPath);
    // Remove any 'vzbl' prefix from the field name
    const displayField = cleanField.replace(/^vzbl/i, "");
    if (func.toLowerCase() === "count") {
      // If the field is like Entity.uuid, use the entity name before .uuid
      const parts = fieldPath.split(".");
      if (parts.length > 1 && parts[1].toLowerCase() === "uuid") {
        return `# of ${pluralizer(parts[0])}`;
      }
      // Otherwise, use the last part, pluralized
      return `# of ${pluralizer(parts.at(-1) ?? "")}`;
    }
    // For other aggregations, use the default logic
    return `${formatAggregationFunction(func)} ${attributeToTitle(
      displayField
    )}`;
  }

  // If no aggregation function, just clean and format the field name
  const cleanField = extractCleanFieldName(field);
  const displayField = cleanField.replace(/^vzbl/i, "");
  return attributeToTitle(displayField);
}

export function limitWords(text: string, wordLimit: number): string {
  if (wordLimit <= 0) return text;

  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
}

/**
 * Formats any metric/field/aggregation string into a human-friendly title.
 * Tries to detect the type and delegates to the appropriate formatter.
 */
export function formatTitle(
  input: string | { attribute: string; target: string }
): string {
  if (!input) return "";

  // If it's an object with attribute/target, use fieldToTitle
  if (typeof input === "object" && "attribute" in input && "target" in input) {
    return fieldToTitle(input);
  }

  // If it looks like an aggregation (e.g., count(Issue.uuid)), use formatAggregationFieldLabel
  if (typeof input === "string" && /[A-Za-z]+\(.+\)/.test(input)) {
    return formatAggregationFieldLabel(input);
  }

  // Otherwise, just use attributeToTitle
  return attributeToTitle(String(input));
}
